import { useParams } from 'react-router-dom';
import {
  useWhatsappGroup,
  useWhatsappGroupMessages,
} from '../hooks/use-whatsapp-groups';
import { Badge } from '@travellegacy/nexops-design-system';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Loader2, MessageSquare, Clock, User, Phone } from 'lucide-react';
import { getStatusBadge } from '../utils/formatting';

export default function GroupDetailPage() {
  const { groupJid } = useParams<{ groupJid: string }>();

  const { data: group, isLoading: groupLoading } = useWhatsappGroup(groupJid!);
  const { data: messages, isLoading: messagesLoading } = useWhatsappGroupMessages(groupJid!, {
    limit: 100,
  });

  if (groupLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!group) {
    return <div className="p-6 text-center">Grupo não encontrado</div>;
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('pt-BR');
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">{group.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            {getStatusBadge(group.status)}
            <span className="text-sm text-muted-foreground flex items-center">
              <Phone className="h-3 w-3 mr-1" />
              {group.groupJid}
            </span>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
               <MessageSquare className="h-4 w-4 mr-2" />
               Total de Mensagens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{group.messageCount || 0}</div>
            <p className="text-xs text-muted-foreground">
              Sincronizado via UAZAPI
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
               <Clock className="h-4 w-4 mr-2" />
               Última Atividade
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">
              {group.lastMessageAt ? formatDate(group.lastMessageAt) : 'Nunca'}
            </div>
            <p className="text-xs text-muted-foreground">
              Última msg equipe: {group.lastTeamMsgAt ? formatDate(group.lastTeamMsgAt) : 'N/A'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
               <User className="h-4 w-4 mr-2" />
               Cliente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">
              {group.clientName || 'Não vinculado'}
            </div>
            {group.clientId && (
              <p className="text-xs text-muted-foreground">ID: {group.clientId}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-bold flex items-center">
          <MessageSquare className="mr-2 h-5 w-5" />
          Histórico de Mensagens (Últimas 100)
        </h2>
        
        {messagesLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : messages && messages.length > 0 ? (
          <Card>
            <CardContent className="p-4 space-y-3">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`p-3 rounded-lg ${
                    msg.senderType === 'TEAM' ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm">{msg.senderName}</span>
                      <Badge variant={msg.senderType === 'TEAM' ? 'outline' : 'default'} className="text-[10px] h-4">
                        {msg.senderType === 'TEAM' ? 'Equipe' : 'Cliente'}
                      </Badge>
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(msg.timestamp).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">
                    {msg.messageText || <em className="text-xs">[{msg.messageType}]</em>}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : (
          <div className="text-center py-12 bg-muted/20 rounded-lg border-2 border-dashed">
            <p className="text-muted-foreground">Nenhuma mensagem encontrada para este grupo.</p>
          </div>
        )}
      </div>
    </div>
  );
}
